import { request } from '@/api/client';
import type { Company } from '@/types/company';

/**
 * 企业信息第三方查询(天眼查/启信宝/聚合数据等)的返回类型。
 * 字段命名贴近数据源返回;前端 service 层将其映射到 Company。
 */
export interface EnrichContact {
  name: string;
  position: string;
}

export interface EnrichCompany {
  name: string;
  creditCode: string;
  legalRepresentative: string;
  address: string;
  website?: string;
  businessScope?: string;
  type?: 'entity' | 'cert' | 'consult' | string;
  status?: string;
  establishedAt?: string;
  contacts?: EnrichContact[];
  /** 数据源标识,当前 mock provider 固定返回 "mock" */
  source: string;
}

export interface EnrichParams {
  name: string;
  creditCode?: string;
  keyword?: string;
}

export interface CompanyListParams {
  search?: string;
  type?: string;
  businessTags?: string;
  sourceTags?: string;
  page?: number;
  pageSize?: number;
}

export interface CompanyListResponse {
  data: Company[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CompanyStats {
  total: number;
  entity: number;
  cert: number;
  consult: number;
}

export interface CreateCompanyPayload {
  name: string;
  creditCode: string;
  address: string;
  website: string;
  type: '实体企业' | '认证机构' | '第三方咨询机构';
  responsiblePerson: string;
  businessTags: string[];
  sourceTags: string[];
  contacts: { name: string; phone: string; position: string }[];
  businessDemands: { description: string; source: string; sourceTag: string; date: string }[];
}

export interface BatchImportPayload {
  companies: CreateCompanyPayload[];
}

export function fetchCompanies(params: CompanyListParams = {}): Promise<CompanyListResponse> {
  return request<CompanyListResponse>('/companies', { params: { ...params } as Record<string, string | number | undefined> });
}

export function fetchCompany(id: string): Promise<Company> {
  return request<Company>(`/companies/${id}`);
}

export function createCompany(payload: CreateCompanyPayload): Promise<Company> {
  return request<Company>('/companies', { method: 'POST', body: payload });
}

export function updateCompany(id: string, payload: CreateCompanyPayload): Promise<Company> {
  return request<Company>(`/companies/${id}`, { method: 'PUT', body: payload });
}

export function deleteCompany(id: string): Promise<void> {
  return request<void>(`/companies/${id}`, { method: 'DELETE' });
}

export function batchImportCompanies(payload: BatchImportPayload): Promise<Company[]> {
  return request<Company[]>('/companies/batch', { method: 'POST', body: payload });
}

export function fetchCompanyStats(): Promise<CompanyStats> {
  return request<CompanyStats>('/companies/stats');
}

/**
 * 一键从第三方数据源(天眼查等)获取企业基础信息。
 * 当前 backend provider=mock,行为:本地确定性 mock,无网络/合规风险。
 * 未来切换 provider 时,前端无需改动。
 * @param params.name 必填,企业名称
 * @param params.signal 来自 React 的 AbortSignal,用于超时/取消
 */
export function assignCompany(id: string, assignedTo: string): Promise<Company> {
  return request<Company>(`/companies/${id}/assign`, { method: 'PUT', body: { assignedTo } });
}

export function enrichCompanyInfo(
  params: EnrichParams & { signal?: AbortSignal },
): Promise<EnrichCompany> {
  const { name, creditCode, keyword, signal } = params;
  return request<EnrichCompany>('/companies/enrich/lookup', {
    method: 'GET',
    params: { name, creditCode, keyword } as Record<string, string | undefined>,
    signal,
  });
}